import { Paper, Stack } from "@mui/material";
import { MultipleAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/MultipleAnglesVideoPlayer";
import { PlaybackControls } from "components/scrapEyeDataDashboard/VideoPlayer/components/PlaybackControls";
import { VideoTimeline } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimeline";
import { useVideoControl } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import {
  PLAYBACK_SCOPE,
  SKIP_BUTTON_SECONDS,
} from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { useSkipAndStepHandlers } from "components/scrapEyeDataDashboard/VideoPlayer/hooks/useSkipAndStepHandlers";
import {
  EventWarning,
  ScrapEyesEvent,
} from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { EventType } from "src/store/api/generatedApi";
import { InferenceInputTimeline } from "./InferenceInputTimeline";
import { AreaLabels } from "components/scrapEyeLabelling/types";
import { VerifiedEventsTimeline } from "./VerifiedEventsTimeline";

interface VerifiedEvent extends AreaLabels {
  id: string;
  start: Dayjs;
  end: Dayjs;
  eventType: EventType;
  material: string | null;
  area: string | null;
  warnings: EventWarning[];
}

interface ViewLabelsVideoPlayerProps {
  location: string;
  startIso: string;
  endIso: string;
  events: ScrapEyesEvent[];
  verifiedEvents: VerifiedEvent[];
  selectedEventId?: string | null;
  onEventClick?: (eventId: string) => void;
  videoStart: Dayjs;
  labellingName?: string;
}

const ViewLabelsVideoPlayerContent = ({
  location,
  startIso,
  endIso,
  events,
  verifiedEvents,
  selectedEventId,
  onEventClick,
  videoStart,
  labellingName,
}: ViewLabelsVideoPlayerProps) => {
  const { state, actions } = useVideoControl();

  useEffect(() => {
    actions.reset();
    const duration = dayjs(endIso).diff(dayjs(startIso), "second");
    actions.updateDuration(duration);
  }, [startIso, endIso, actions]);

  const {
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
  } = useSkipAndStepHandlers({ skipSeconds: SKIP_BUTTON_SECONDS });

  return (
    <Stack spacing={2}>
      <MultipleAnglesVideoPlayer
        hotkeyScope={PLAYBACK_SCOPE}
        location={location}
        startIso={startIso}
        endIso={endIso}
        events={events}
        videoStart={videoStart}
        showVideoControls={false}
      />

      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <VideoTimeline
            currentTime={state.currentTime}
            duration={state.duration}
            events={events}
            selectedEventId={selectedEventId}
            onSeek={actions.seekTo}
            onEventClick={onEventClick}
            videoStart={videoStart}
            colorEventsBy="material"
          />

          {labellingName ? (
            <InferenceInputTimeline
              labellingName={labellingName}
              startIso={startIso}
              endIso={endIso}
              videoStart={videoStart}
              duration={state.duration}
            />
          ) : null}

          <VerifiedEventsTimeline
            verifiedEvents={verifiedEvents}
            videoStart={videoStart}
            currentTime={state.currentTime}
            duration={state.duration}
          />

          <PlaybackControls
            isPlaying={state.isPlaying}
            playbackRate={state.playbackRate}
            onTogglePlayPause={actions.togglePlayPause}
            onSkipBackward={handleSkipBackward}
            onSkipForward={handleSkipForward}
            onSpeedChange={actions.setPlaybackRate}
            videoStart={videoStart}
            onStepBackward={handleStepBackward}
            onStepForward={handleStepForward}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export const ViewLabelsVideoPlayer = (props: ViewLabelsVideoPlayerProps) => {
  return <ViewLabelsVideoPlayerContent {...props} />;
};
